import { useState, useEffect, useContext } from "react";
import { Link } from "react-router-dom";
import { AuthContext } from "../../../context/authcontext";
import { API_BASE_URL } from "../../../environment";
import Select, { components, MultiValue } from "react-select";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import Table from "../../../core/common/dataTable/index";
import { getLifecycleTypeName } from "../../../utils/lifecycleUtils";

interface Team {
    team_id: number;
    name: string;
}

interface BotChannelAccount {
    external_chat_bot_channel_account_id: number;
    account_name: string;
    external_chat_platform_id: number;
    external_chat_channel?: {
        external_chat_channel_id: number;
        external_chat_channel_display_name: string;
        external_chat_channel_key: string;
    };
    external_chat_platform?: {
        external_chat_platform_id: number;
        platform_display_name: string;
        platform_key: string;
    };
}

interface TeamMapping {
    external_chat_bot_channel_account_team_mapping_id: number;
    external_chat_bot_channel_account: {
        external_chat_bot_channel_account_id: number;
        account_name: string;
        external_chat_platform_key: string;
        external_chat_channel_key: string;
    };
    assigned_team: {
        assigned_team_id: number;
        team_name: string;
    };
    is_active: boolean;
    tenant_id: number;
}

interface CrmEntity {
    crm_entity_id: number;
    crm_entity_key: string;
    crm_entity_display_name: string;
}

interface AutoCreateEntityConfig {
    external_chat_auto_create_entity_config_id: number;
    is_global: boolean;
    is_active: boolean;
    tenant_id: number;
    crm_entity: {
        crm_entity_id: number;
        crm_entity_key: string;
        crm_entity_display_name: string;
    };
}

interface AccountOption {
    value: number;
    label: string;
    key?: string;
    platformId?: number;
    platformKey?: string;
}

const TeamAllocationSettings = () => {
    const { selectedTenantId, auth } = useContext(AuthContext);
    const MySwal = withReactContent(Swal);

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [teams, setTeams] = useState<Team[]>([]);
    const [channels, setChannels] = useState<BotChannelAccount[]>([]);
    const [teamMappings, setTeamMappings] = useState<TeamMapping[]>([]);
    const [selectedTeamId, setSelectedTeamId] = useState<number | "">("")
    const [selectedAccountIds, setSelectedAccountIds] = useState<number[]>([]);
    const [selectedMappingIds, setSelectedMappingIds] = useState<number[]>([]);
    const [bulkDeleting, setBulkDeleting] = useState(false);
    const [autoAssignChatOnReply, setAutoAssignChatOnReply] = useState(false);
    const [autoAssignLoading, setAutoAssignLoading] = useState(false);
    const [isRoundRobinOnChat, setIsRoundRobinOnChat] = useState(false);
    const [roundRobinLoading, setRoundRobinLoading] = useState(false);
    const [crmEntities, setCrmEntities] = useState<CrmEntity[]>([]);
    const [autoCreateConfigs, setAutoCreateConfigs] = useState<AutoCreateEntityConfig[]>([]);
    const [autoCreateLoading, setAutoCreateLoading] = useState<Record<string, boolean>>({});

    const fetchTeams = async () => {
        if (!selectedTenantId) return;
        try {
            const response = await fetch(
                `${API_BASE_URL}/team/?tenant_id=${selectedTenantId}`,
                {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Basic ${localStorage.getItem("authCred")}`,
                    },
                }
            );
            if (response.ok) {
                const data = await response.json();
                setTeams(data.results || []);
            }
        } catch (error) {
            console.error("Error fetching teams:", error);
        }
    };

    const fetchChannels = async () => {
        if (!selectedTenantId) return;
        try {
            const response = await fetch(
                `${API_BASE_URL}/external-chat-bot-channel-account/?tenant_id=${selectedTenantId}&is_active=true&chat_type_key=EXTERNAL`,
                {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Basic ${localStorage.getItem("authCred")}`,
                    },
                }
            );
            if (response.ok) {
                const data = await response.json();
                setChannels(data.results || data || []);
            }
        } catch (error) {
            console.error("Error fetching channels:", error);
        }
    };

    const fetchTeamMappings = async () => {
        if (!selectedTenantId) return;
        try {
            const response = await fetch(
                `${API_BASE_URL}/external-chat-bot-channel-account-team-mapping/?tenant_id=${selectedTenantId}`,
                {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Basic ${localStorage.getItem("authCred")}`,
                    },
                }
            );
            if (response.ok) {
                const data = await response.json();
                setTeamMappings(data.results || []);
            }
        } catch (error) {
            console.error("Error fetching team mappings:", error);
        }
    };

    const fetchAutoAssignSetting = async () => {
        if (!selectedTenantId) return;
        try {
            const response = await fetch(
                `${API_BASE_URL}/tenant/?tenant_id=${selectedTenantId}`,
                {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Basic ${localStorage.getItem("authCred")}`,
                    },
                }
            );
            if (response.ok) {
                const data = await response.json();
                if (data.results && data.results.length > 0) {
                    setAutoAssignChatOnReply(data.results[0].auto_assign_chat_on_reply || false);
                    setIsRoundRobinOnChat(data.results[0].is_round_robin_on_chat || false);
                }
            }
        } catch (error) {
            console.error("Error fetching auto assign setting:", error);
        }
    };

    const fetchCrmEntities = async () => {
        if (!selectedTenantId) return;
        try {
            const response = await fetch(
                `${API_BASE_URL}/crm-entity/`,
                {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Basic ${localStorage.getItem("authCred")}`,
                    },
                }
            );
            if (response.ok) {
                const data = await response.json();
                setCrmEntities(data.results || []);
            }
        } catch (error) {
            console.error("Error fetching CRM entities:", error);
        }
    };

    const fetchAutoCreateConfigs = async () => {
        if (!selectedTenantId) return;
        try {
            const response = await fetch(
                `${API_BASE_URL}/external-chat-auto-create-entity-config/?tenant_id=${selectedTenantId}`,
                {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Basic ${localStorage.getItem("authCred")}`,
                    },
                }
            );
            if (response.ok) {
                const data = await response.json();
                setAutoCreateConfigs(data.results || data || []);
            }
        } catch (error) {
            console.error("Error fetching auto create entity configs:", error);
        }
    };

    const handleAutoCreateEntityToggle = async (entityKey: string) => {
        if (!selectedTenantId) return;

        const entity = crmEntities.find(e => e.crm_entity_key === entityKey);
        if (!entity) return;

        setAutoCreateLoading(prev => ({ ...prev, [entityKey]: true }));

        const existingConfig = autoCreateConfigs.find(
            c => c.crm_entity?.crm_entity_key === entityKey
        );

        try {
            let response;
            if (existingConfig) {
                response = await fetch(
                    `${API_BASE_URL}/external-chat-auto-create-entity-config/${existingConfig.external_chat_auto_create_entity_config_id}/`,
                    {
                        method: "PATCH",
                        headers: {
                            "Content-Type": "application/json",
                            Authorization: `Basic ${localStorage.getItem("authCred")}`,
                        },
                        body: JSON.stringify({
                            is_active: !existingConfig.is_active,
                        }),
                    }
                );
            } else {
                response = await fetch(
                    `${API_BASE_URL}/external-chat-auto-create-entity-config/`,
                    {
                        method: "POST",
                        headers: {
                            "Content-Type": "application/json",
                            Authorization: `Basic ${localStorage.getItem("authCred")}`,
                        },
                        body: JSON.stringify({
                            is_global: true,
                            is_active: true,
                            tenant_id: selectedTenantId,
                            crm_entity_id: entity.crm_entity_id,
                        }),
                    }
                );
            }

            if (response.ok) {
                await fetchAutoCreateConfigs();
                MySwal.fire({
                    title: "Success!",
                    text: `Auto-create ${entity.crm_entity_display_name} ${existingConfig ? (existingConfig.is_active ? "disabled" : "enabled") : "enabled"} successfully!`,
                    icon: "success",
                    confirmButtonText: "OK",
                });
            } else {
                MySwal.fire({
                    title: "Error!",
                    text: `Failed to update auto-create ${entity.crm_entity_display_name} setting.`,
                    icon: "error",
                    confirmButtonText: "OK",
                });
            }
        } catch (error) {
            MySwal.fire({
                title: "Error!",
                text: "An error occurred while updating the setting.",
                icon: "error",
                confirmButtonText: "OK",
            });
        } finally {
            setAutoCreateLoading(prev => ({ ...prev, [entityKey]: false }));
        }
    };

    const isAutoCreateActive = (entityKey: string): boolean => {
        const config = autoCreateConfigs.find(c => c.crm_entity?.crm_entity_key === entityKey);
        return config?.is_active || false;
    };

    const handleAutoAssignToggle = async () => {
        if (!selectedTenantId) return;

        setAutoAssignLoading(true);
        const newValue = !autoAssignChatOnReply;

        try {
            const response = await fetch(
                `${API_BASE_URL}/tenant/${selectedTenantId}/`,
                {
                    method: "PATCH",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Basic ${localStorage.getItem("authCred")}`,
                    },
                    body: JSON.stringify({
                        auto_assign_chat_on_reply: newValue,
                    }),
                }
            );

            if (response.ok) {
                setAutoAssignChatOnReply(newValue);
                if (newValue) {
                    setIsRoundRobinOnChat(false);
                }
                MySwal.fire({
                    title: "Success!",
                    text: `Auto Assign Chat on Reply ${newValue ? "enabled" : "disabled"} successfully!`,
                    icon: "success",
                    confirmButtonText: "OK",
                });
            } else {
                MySwal.fire({
                    title: "Error!",
                    text: "Failed to update setting.",
                    icon: "error",
                    confirmButtonText: "OK",
                });
            }
        } catch (error) {
            MySwal.fire({
                title: "Error!",
                text: "An error occurred while updating the setting.",
                icon: "error",
                confirmButtonText: "OK",
            });
        } finally {
            setAutoAssignLoading(false);
        }
    };

    const handleRoundRobinToggle = async () => {
        if (!selectedTenantId) return;

        setRoundRobinLoading(true);
        const newValue = !isRoundRobinOnChat;

        try {
            const response = await fetch(
                `${API_BASE_URL}/tenant/${selectedTenantId}/`,
                {
                    method: "PATCH",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Basic ${localStorage.getItem("authCred")}`,
                    },
                    body: JSON.stringify({
                        is_round_robin_on_chat: newValue,
                    }),
                }
            );

            if (response.ok) {
                setIsRoundRobinOnChat(newValue);
                if (newValue) {
                    setAutoAssignChatOnReply(false);
                }
                MySwal.fire({
                    title: "Success!",
                    text: `Round Robin Assignment ${newValue ? "enabled" : "disabled"} successfully!`,
                    icon: "success",
                    confirmButtonText: "OK",
                });
            } else {
                MySwal.fire({
                    title: "Error!",
                    text: "Failed to update setting.",
                    icon: "error",
                    confirmButtonText: "OK",
                });
            }
        } catch (error) {
            MySwal.fire({
                title: "Error!",
                text: "An error occurred while updating the setting.",
                icon: "error",
                confirmButtonText: "OK",
            });
        } finally {
            setRoundRobinLoading(false);
        }
    };

    const fetchAll = async () => {
        setLoading(true);
        await Promise.all([fetchTeams(), fetchChannels(), fetchTeamMappings(), fetchAutoAssignSetting(), fetchCrmEntities(), fetchAutoCreateConfigs()]);
        setLoading(false);
    };

    useEffect(() => {
        fetchAll();
    }, [selectedTenantId]);

    // When the user selects a team, auto-populate the accounts already mapped to it
    const handleTeamChange = (teamId: number | "") => {
        setSelectedTeamId(teamId);
        if (!teamId) {
            setSelectedAccountIds([]);
            return;
        }
        // Pre-check accounts already mapped to this team
        const mappedAccountIds = teamMappings
            .filter((m) => m.assigned_team?.assigned_team_id === teamId)
            .map((m) => m.external_chat_bot_channel_account.external_chat_bot_channel_account_id);
        setSelectedAccountIds(mappedAccountIds);
    };

    const handleSaveAllocation = async () => {
        if (!selectedTeamId || !selectedTenantId) {
            MySwal.fire({
                title: "Error",
                text: "Please select a team.",
                icon: "error",
                confirmButtonText: "OK",
            });
            return;
        }

        if (selectedAccountIds.length === 0) {
            // If nothing is selected, ask for confirmation since this will remove all mappings
            const existingMappings = teamMappings.filter(
                (m) => m.assigned_team?.assigned_team_id === selectedTeamId
            );
            if (existingMappings.length > 0) {
                const confirm = await MySwal.fire({
                    title: "Remove all allocations?",
                    text: "No accounts are selected. This will remove all existing allocations for this team.",
                    icon: "warning",
                    showCancelButton: true,
                    confirmButtonText: "Yes, remove all",
                    cancelButtonText: "Cancel",
                });
                if (!confirm.isConfirmed) return;
            } else {
                MySwal.fire({
                    title: "Error",
                    text: "Please select at least one account.",
                    icon: "error",
                    confirmButtonText: "OK",
                });
                return;
            }
        }

        setSaving(true);
        try {
            // Existing mappings for this team
            const existingMappings = teamMappings.filter(
                (m) => m.assigned_team?.assigned_team_id === selectedTeamId
            );
            const existingAccountIds = new Set(
                existingMappings.map((m) => m.external_chat_bot_channel_account.external_chat_bot_channel_account_id)
            );

            // Build inserts: accounts that are newly selected
            const inserts = selectedAccountIds
                .filter((accountId) => !existingAccountIds.has(accountId))
                .map((accountId) => {
                    const channel = channels.find(
                        (c) => c.external_chat_bot_channel_account_id === accountId
                    );
                    return {
                        tenant_id: selectedTenantId,
                        assigned_team_id: selectedTeamId,
                        external_chat_bot_channel_account_id: accountId,
                        external_chat_platform_id: channel?.external_chat_platform_id,
                    };
                });

            // Build deletes: mappings whose account is no longer selected
            const selectedSet = new Set(selectedAccountIds);
            const deletes = existingMappings
                .filter(
                    (m) => !selectedSet.has(m.external_chat_bot_channel_account.external_chat_bot_channel_account_id)
                )
                .map((m) => m.external_chat_bot_channel_account_team_mapping_id);

            if (inserts.length === 0 && deletes.length === 0) {
                MySwal.fire({
                    title: "No Changes",
                    text: "No changes detected. The selected accounts match the existing allocations.",
                    icon: "info",
                    confirmButtonText: "OK",
                });
                setSaving(false);
                return;
            }

            const response = await fetch(
                `${API_BASE_URL}/external-chat-bot-channel-account-team-mapping/batch-operation/`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Basic ${localStorage.getItem("authCred")}`,
                    },
                    body: JSON.stringify({ inserts, deletes }),
                }
            );

            if (response.ok) {
                MySwal.fire({
                    title: "Success!",
                    text: "Team allocation updated successfully!",
                    icon: "success",
                    confirmButtonText: "OK",
                });
                setSelectedTeamId("");
                setSelectedAccountIds([]);
                await fetchTeamMappings();
            } else {
                MySwal.fire({
                    title: "Error!",
                    text: "Failed to update team allocation.",
                    icon: "error",
                    confirmButtonText: "OK",
                });
            }
        } catch (error) {
            MySwal.fire({
                title: "Error!",
                text: "An error occurred while updating team allocation.",
                icon: "error",
                confirmButtonText: "OK",
            });
        } finally {
            setSaving(false);
        }
    };

    const handleDeleteTeamMapping = async (id: number) => {
        const result = await MySwal.fire({
            title: "Are you sure?",
            text: "Do you want to remove this team allocation?",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#d33",
            cancelButtonColor: "#3085d6",
            confirmButtonText: "Yes, remove it!",
            cancelButtonText: "Cancel",
        });

        if (result.isConfirmed) {
            try {
                const response = await fetch(
                    `${API_BASE_URL}/external-chat-bot-channel-account-team-mapping/${id}/`,
                    {
                        method: "DELETE",
                        headers: {
                            "Content-Type": "application/json",
                            Authorization: `Basic ${localStorage.getItem("authCred")}`,
                        },
                    }
                );

                if (response.ok) {
                    MySwal.fire({
                        title: "Deleted!",
                        text: "Team allocation removed successfully.",
                        icon: "success",
                        confirmButtonText: "OK",
                    });
                    setSelectedMappingIds(prev => prev.filter(mid => mid !== id));
                    await fetchTeamMappings();
                } else {
                    MySwal.fire({
                        title: "Error!",
                        text: "Failed to remove team allocation.",
                        icon: "error",
                        confirmButtonText: "OK",
                    });
                }
            } catch (error) {
                MySwal.fire({
                    title: "Error!",
                    text: "An error occurred while removing team allocation.",
                    icon: "error",
                    confirmButtonText: "OK",
                });
            }
        }
    };

    const handleBulkDelete = async () => {
        if (selectedMappingIds.length === 0) return;

        const result = await MySwal.fire({
            title: "Are you sure?",
            text: `Do you want to remove ${selectedMappingIds.length} team allocation(s)?`,
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#d33",
            cancelButtonColor: "#3085d6",
            confirmButtonText: "Yes, remove them!",
            cancelButtonText: "Cancel",
        });

        if (!result.isConfirmed) return;

        setBulkDeleting(true);
        try {
            const response = await fetch(
                `${API_BASE_URL}/external-chat-bot-channel-account-team-mapping/batch-operation/`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Basic ${localStorage.getItem("authCred")}`,
                    },
                    body: JSON.stringify({ inserts: [], deletes: selectedMappingIds }),
                }
            );

            if (response.ok) {
                MySwal.fire({
                    title: "Deleted!",
                    text: `${selectedMappingIds.length} team allocation(s) removed successfully.`,
                    icon: "success",
                    confirmButtonText: "OK",
                });
                setSelectedMappingIds([]);
                await fetchTeamMappings();
            } else {
                MySwal.fire({
                    title: "Error!",
                    text: "Failed to remove team allocations.",
                    icon: "error",
                    confirmButtonText: "OK",
                });
            }
        } catch (error) {
            MySwal.fire({
                title: "Error!",
                text: "An error occurred while removing team allocations.",
                icon: "error",
                confirmButtonText: "OK",
            });
        } finally {
            setBulkDeleting(false);
        }
    };

    const toggleMappingSelection = (id: number) => {
        setSelectedMappingIds(prev =>
            prev.includes(id) ? prev.filter(mid => mid !== id) : [...prev, id]
        );
    };

    const toggleSelectAll = () => {
        if (selectedMappingIds.length === teamMappings.length) {
            setSelectedMappingIds([]);
        } else {
            setSelectedMappingIds(teamMappings.map(m => m.external_chat_bot_channel_account_team_mapping_id));
        }
    };

    const getChannelIcon = (channelKey: string) => {
        if (!channelKey) return "fa-solid fa-message";
        switch (channelKey.toUpperCase()) {
            case "SMS":
                return "fa-solid fa-message";
            case "WHATSAPP":
                return "fa-brands fa-whatsapp";
            case "FACEBOOK":
                return "fa-brands fa-facebook";
            case "INSTAGRAM":
                return "fa-brands fa-instagram";
            case "LINE":
                return "fa-brands fa-line";
            case "SLACK":
                return "fa-brands fa-slack";
            case "TELEGRAM":
                return "fa-brands fa-telegram";
            default:
                return "fa-solid fa-message";
        }
    };


    const getChannelColor = (channelKey: string) => {
        if (!channelKey) return "#666666";
        switch (channelKey.toUpperCase()) {
            case "SMS":
                return "#4caf50";
            case "WHATSAPP":
                return "#25D366";
            case "FACEBOOK":
                return "#1877F2";
            case "INSTAGRAM":
                return "#E4405F";
            case "LINE":
                return "#00B900";
            case "SLACK":
                return "#4A154B";
            case "TELEGRAM":
                return "#2AABEE";
            default:
                return "#666666";
        }
    };

    const renderPlatformIcon = (platformKey: string | undefined, channelKey: string | undefined, size: number = 18, containerStyle: any = { marginRight: '8px' }) => {
        const isEngati = !platformKey || platformKey === 'ENGATI';
        if (isEngati) {
            return (
                <i
                    className={getChannelIcon(channelKey || "")}
                    style={{
                        color: getChannelColor(channelKey || ""),
                        fontSize: `${size}px`,
                        ...containerStyle
                    }}
                ></i>
            );
        }
        const platformIconMap: Record<string, string> = {
            LAZADA: 'Lazada',
            TIKTOK: 'TikTok',
            SHOPEE: 'Shopee',
            GOOGLE_CHAT: 'Google-Chat',
        };
        const iconFileName = platformIconMap[platformKey.toUpperCase()] || (platformKey.charAt(0).toUpperCase() + platformKey.slice(1).toLowerCase());
        return (
            <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(255,255,255,0.9)', padding: '2px', borderRadius: '4px', ...containerStyle }}>
                <img
                    src={`/assets/img/platform-icons/${iconFileName}.png`}
                    alt={platformKey}
                    style={{ width: `${size}px`, height: `${size}px`, objectFit: 'contain' }}
                    onError={(e) => {
                        const target = e.currentTarget;
                        target.style.display = 'none';
                        const parent = target.parentElement;
                        if (parent) {
                            const fallback = document.createElement('i');
                            fallback.className = 'fa-solid fa-globe';
                            fallback.style.color = '#666666';
                            parent.appendChild(fallback);
                        }
                    }}
                />
            </span>
        );
    };

    const accountOptions: AccountOption[] = channels.map(chan => ({
        value: chan.external_chat_bot_channel_account_id,
        label: chan.account_name,
        key: chan.external_chat_channel?.external_chat_channel_key,
        platformId: chan.external_chat_platform_id,
        platformKey: chan.external_chat_platform?.platform_key,
    }));

    const formatChannelOption = (option: any) => {
        const platformKey = option.platformKey || option.data?.platformKey;
        const channelKey = option.key || option.data?.key;
        return (
            <div className="d-flex align-items-center">
                {renderPlatformIcon(platformKey, channelKey, 18, { marginRight: '10px' })}
                <span>{option.label}</span>
            </div>
        );
    };

    // Custom Option component with a checkbox
    const CheckboxOption = (props: any) => (
        <components.Option {...props}>
            <div className="d-flex align-items-center">
                <input
                    type="checkbox"
                    checked={props.isSelected}
                    onChange={() => null}
                    style={{ marginRight: '10px', accentColor: '#0ea5e9' }}
                />
                {renderPlatformIcon(props.data.platformKey, props.data.key, 18, { marginRight: '10px' })}
                <span>{props.data.label}</span>
            </div>
        </components.Option>
    );

    const customSelectStyles = {
        control: (base: any) => ({
            ...base,
            minHeight: '38px',
            borderRadius: '6px',
            borderColor: '#e9ecef',
            boxShadow: 'none',
            '&:hover': {
                borderColor: '#0ea5e9'
            }
        }),
        option: (base: any, state: any) => ({
            ...base,
            backgroundColor: state.isFocused ? '#f0f9ff' : 'white',
            color: '#333',
            cursor: 'pointer',
            fontSize: '14px',
            '&:active': {
                backgroundColor: '#e0f2fe'
            }
        }),
        multiValue: (base: any) => ({
            ...base,
            backgroundColor: '#e0f2fe',
            borderRadius: '4px',
        }),
        multiValueLabel: (base: any) => ({
            ...base,
            color: '#0369a1',
            fontSize: '13px',
        }),
        multiValueRemove: (base: any) => ({
            ...base,
            color: '#0369a1',
            ':hover': {
                backgroundColor: '#bae6fd',
                color: '#0c4a6e',
            },
        }),
        placeholder: (base: any) => ({
            ...base,
            fontSize: '14px'
        })
    };

    const columns = [
        {
            title: (
                <input
                    type="checkbox"
                    checked={teamMappings.length > 0 && selectedMappingIds.length === teamMappings.length}
                    onChange={toggleSelectAll}
                    style={{ accentColor: '#0ea5e9', cursor: 'pointer' }}
                />
            ),
            dataIndex: "select",
            width: 50,
            render: (_: any, record: TeamMapping) => (
                <input
                    type="checkbox"
                    checked={selectedMappingIds.includes(record.external_chat_bot_channel_account_team_mapping_id)}
                    onChange={() => toggleMappingSelection(record.external_chat_bot_channel_account_team_mapping_id)}
                    style={{ accentColor: '#0ea5e9', cursor: 'pointer' }}
                />
            ),
        },
        {
            title: "Team",
            dataIndex: "assigned_team",
            render: (assigned_team: { team_name: string }) => (
                <span>{assigned_team?.team_name || "N/A"}</span>
            ),
        },
        {
            title: "Account",
            dataIndex: "external_chat_bot_channel_account",
            render: (external_chat_bot_channel_account: { account_name: string; external_chat_platform_key: string; external_chat_channel_key: string }) => (
                <div className="d-flex align-items-center">
                    {renderPlatformIcon(external_chat_bot_channel_account?.external_chat_platform_key, external_chat_bot_channel_account?.external_chat_channel_key, 18, { marginRight: "8px" })}
                    <span>{external_chat_bot_channel_account?.account_name || "N/A"}</span>
                </div>
            ),
        },
        {
            title: "Actions",
            dataIndex: "actions",
            render: (text: string, record: TeamMapping) => (
                <div className="dropdown table-action">
                    <Link
                        to="#"
                        className="action-icon"
                        data-bs-toggle="dropdown"
                        aria-expanded="false"
                    >
                        <i className="fa fa-ellipsis-v"></i>
                    </Link>
                    <div className="dropdown-menu dropdown-menu-end">
                        <Link
                            className="dropdown-item"
                            to="#"
                            onClick={() => handleDeleteTeamMapping(record.external_chat_bot_channel_account_team_mapping_id)}
                        >
                            <i className="ti ti-trash text-danger"></i> Delete
                        </Link>
                    </div>
                </div>
            ),
        },
    ];

    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "200px" }}>
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
        );
    }

    return (
        <>
            <div className="border-bottom mb-3 pb-3">
                <h4 className="fw-semibold mb-1">Team Allocation</h4>
                <p>Assign teams to chat channels</p>
            </div>


            <div className="border rounded p-3 mb-4 mt-4">
                <div className="row gy-3">
                    <div className="col-sm-8">
                        <div className="d-flex align-items-center">
                            <span className="avatar avtar-lg border me-2 flex-shrink-0">
                                <i
                                    className="ti ti-user-check text-primary"
                                    style={{ fontSize: "24px" }}
                                ></i>
                            </span>
                            <div>
                                <h6 className="fw-medium mb-1">
                                    Auto Assign Chat on Reply
                                </h6>
                                <p className="text-muted mb-0" style={{ fontSize: "13px" }}>
                                    When enabled, the agent who sends the reply will be automatically assigned to the chat conversation
                                </p>
                            </div>
                        </div>
                    </div>
                    <div className="col-sm-4">
                        <div className="d-flex align-items-center justify-content-end h-100">
                            <div className="status-toggle">
                                <input
                                    type="checkbox"
                                    id="autoAssignChat"
                                    className="check"
                                    checked={autoAssignChatOnReply}
                                    onChange={handleAutoAssignToggle}
                                    disabled={autoAssignLoading}
                                />
                                <label
                                    htmlFor="autoAssignChat"
                                    className="checktoggle"
                                >
                                    {" "}
                                </label>
                            </div>
                        </div>
                    </div>
                </div>
                <hr className="my-4" />
                <div className="row gy-3">
                    <div className="col-sm-8">
                        <div className="d-flex align-items-center">
                            <span className="avatar avtar-lg border me-2 flex-shrink-0">
                                <i
                                    className="ti ti-users text-primary"
                                    style={{ fontSize: "24px" }}
                                ></i>
                            </span>
                            <div>
                                <h6 className="fw-medium mb-1">
                                    Round Robin Assignment
                                </h6>
                                <p className="text-muted mb-0" style={{ fontSize: "13px" }}>
                                    When enabled, chats will be automatically distributed sequentially among available team members
                                </p>
                            </div>
                        </div>
                    </div>
                    <div className="col-sm-4">
                        <div className="d-flex align-items-center justify-content-end h-100">
                            <div className="status-toggle">
                                <input
                                    type="checkbox"
                                    id="roundRobinAssignment"
                                    className="check"
                                    checked={isRoundRobinOnChat}
                                    onChange={handleRoundRobinToggle}
                                    disabled={roundRobinLoading}
                                />
                                <label
                                    htmlFor="roundRobinAssignment"
                                    className="checktoggle"
                                >
                                    {" "}
                                </label>
                            </div>
                        </div>
                    </div>
                </div>

                {isRoundRobinOnChat && (
                    <>
                        <hr className="my-4" />
                        <div>
                            <div className="d-flex align-items-center mb-3">
                                <span className="avatar avtar-lg border me-2 flex-shrink-0">
                                    <i
                                        className="ti ti-sparkles text-primary"
                                        style={{ fontSize: "24px" }}
                                    ></i>
                                </span>
                                <div>
                                    <h6 className="fw-medium mb-1">
                                        Auto-Create on New Chat
                                    </h6>
                                    <p className="text-muted mb-0" style={{ fontSize: "13px" }}>
                                        Automatically create a {getLifecycleTypeName("LEAD")} or Contact record when a new chat is received and assign according to the Round Robin Assignment
                                    </p>
                                </div>
                            </div>

                            <div className="row g-3" style={{ paddingLeft: "52px" }}>
                                <div className="col-sm-6">
                                    <div
                                        className="border rounded p-3 d-flex align-items-center justify-content-between"
                                        style={{
                                            background: isAutoCreateActive("LEAD")
                                                ? "linear-gradient(135deg, #fef3c7 0%, #fffbeb 100%)"
                                                : "#fafafa",
                                            borderColor: isAutoCreateActive("LEAD") ? "#f59e0b" : "#e9ecef",
                                            transition: "all 0.3s ease",
                                        }}
                                    >
                                        <div className="d-flex align-items-center">
                                            <span
                                                className="d-inline-flex align-items-center justify-content-center me-2 flex-shrink-0"
                                                style={{
                                                    width: "36px",
                                                    height: "36px",
                                                    borderRadius: "8px",
                                                    background: isAutoCreateActive("LEAD")
                                                        ? "rgba(245, 158, 11, 0.15)"
                                                        : "rgba(0, 0, 0, 0.05)",
                                                    transition: "all 0.3s ease",
                                                }}
                                            >
                                                <i
                                                    className="ti ti-chart-arcs"
                                                    style={{
                                                        fontSize: "18px",
                                                        color: isAutoCreateActive("LEAD") ? "#f59e0b" : "#999",
                                                    }}
                                                ></i>
                                            </span>
                                            <div>
                                                <h6 className="fw-medium mb-0" style={{ fontSize: "14px" }}>{getLifecycleTypeName("LEAD")}</h6>
                                                <p className="text-muted mb-0" style={{ fontSize: "12px" }}>
                                                    Create {getLifecycleTypeName("LEAD").toLowerCase()} automatically
                                                </p>
                                            </div>
                                        </div>
                                        <div className="d-flex align-items-center justify-content-end" style={{ width: '44px' }}>
                                            {autoCreateLoading["LEAD"] ? (
                                                <div className="spinner-border spinner-border-sm text-warning" role="status">
                                                    <span className="visually-hidden">Loading...</span>
                                                </div>
                                            ) : (
                                                <div className="status-toggle">
                                                    <input
                                                        type="checkbox"
                                                        id="autoCreateLead"
                                                        className="check"
                                                        checked={isAutoCreateActive("LEAD")}
                                                        onChange={() => handleAutoCreateEntityToggle("LEAD")}
                                                        disabled={autoCreateLoading["LEAD"]}
                                                    />
                                                    <label
                                                        htmlFor="autoCreateLead"
                                                        className="checktoggle"
                                                    >
                                                        {" "}
                                                    </label>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                <div className="col-sm-6">
                                    <div
                                        className="border rounded p-3 d-flex align-items-center justify-content-between"
                                        style={{
                                            background: isAutoCreateActive("CONTACT")
                                                ? "linear-gradient(135deg, #dbeafe 0%, #eff6ff 100%)"
                                                : "#fafafa",
                                            borderColor: isAutoCreateActive("CONTACT") ? "#3b82f6" : "#e9ecef",
                                            transition: "all 0.3s ease",
                                        }}
                                    >
                                        <div className="d-flex align-items-center">
                                            <span
                                                className="d-inline-flex align-items-center justify-content-center me-2 flex-shrink-0"
                                                style={{
                                                    width: "36px",
                                                    height: "36px",
                                                    borderRadius: "8px",
                                                    background: isAutoCreateActive("CONTACT")
                                                        ? "rgba(59, 130, 246, 0.15)"
                                                        : "rgba(0, 0, 0, 0.05)",
                                                    transition: "all 0.3s ease",
                                                }}
                                            >
                                                <i
                                                    className="ti ti-address-book"
                                                    style={{
                                                        fontSize: "18px",
                                                        color: isAutoCreateActive("CONTACT") ? "#3b82f6" : "#999",
                                                    }}
                                                ></i>
                                            </span>
                                            <div>
                                                <h6 className="fw-medium mb-0" style={{ fontSize: "14px" }}>Contact</h6>
                                                <p className="text-muted mb-0" style={{ fontSize: "12px" }}>
                                                    Create Contact automatically
                                                </p>
                                            </div>
                                        </div>
                                        <div className="d-flex align-items-center justify-content-end" style={{ width: '44px' }}>
                                            {autoCreateLoading["CONTACT"] ? (
                                                <div className="spinner-border spinner-border-sm text-primary" role="status">
                                                    <span className="visually-hidden">Loading...</span>
                                                </div>
                                            ) : (
                                                <div className="status-toggle">
                                                    <input
                                                        type="checkbox"
                                                        id="autoCreateContact"
                                                        className="check"
                                                        checked={isAutoCreateActive("CONTACT")}
                                                        onChange={() => handleAutoCreateEntityToggle("CONTACT")}
                                                        disabled={autoCreateLoading["CONTACT"]}
                                                    />
                                                    <label
                                                        htmlFor="autoCreateContact"
                                                        className="checktoggle"
                                                    >
                                                        {" "}
                                                    </label>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </>
                )}
            </div>

            <div className="row mb-4">
                <div className="col-md-5">
                    <label className="form-label">Select Team</label>
                    <select
                        className="form-select"
                        value={selectedTeamId}
                        onChange={(e) => handleTeamChange(e.target.value ? Number(e.target.value) : "")}
                    >
                        <option value="">Select a team...</option>
                        {teams.map((team) => (
                            <option key={team.team_id} value={team.team_id}>
                                {team.name}
                            </option>
                        ))}
                    </select>
                </div>
                <div className="col-md-5">
                    <label className="form-label">Select Accounts</label>
                    <Select<AccountOption, true>
                        classNamePrefix="react-select"
                        options={accountOptions}
                        isMulti
                        closeMenuOnSelect={false}
                        hideSelectedOptions={false}
                        components={{ Option: CheckboxOption }}
                        value={accountOptions.filter(opt => selectedAccountIds.includes(opt.value))}
                        onChange={(selected: MultiValue<AccountOption>) => {
                            setSelectedAccountIds(selected ? selected.map(s => s.value) : []);
                        }}
                        styles={customSelectStyles}
                        placeholder="Select accounts..."
                        isDisabled={!selectedTeamId}
                    />
                </div>
                <div className="col-md-2 d-flex align-items-end">
                    <button
                        type="button"
                        className="btn btn-primary w-100"
                        onClick={handleSaveAllocation}
                        disabled={saving || !selectedTeamId}
                    >
                        {saving ? "Saving..." : "Save Allocation"}
                    </button>
                </div>
            </div>

            {selectedMappingIds.length > 0 && (
                <div className="d-flex align-items-center mb-3 p-2 border rounded" style={{ backgroundColor: '#fff8f8' }}>
                    <span className="me-3" style={{ fontSize: '14px' }}>
                        <strong>{selectedMappingIds.length}</strong> allocation(s) selected
                    </span>
                    <button
                        type="button"
                        className="btn btn-danger btn-sm"
                        onClick={handleBulkDelete}
                        disabled={bulkDeleting}
                    >
                        <i className="ti ti-trash me-1"></i>
                        {bulkDeleting ? "Deleting..." : "Delete Selected"}
                    </button>
                </div>
            )}

            <div className="table-responsive custom-table">
                <Table
                    dataSource={teamMappings}
                    columns={columns}
                    rowKey={(record: TeamMapping) => record.external_chat_bot_channel_account_team_mapping_id}
                    rowSelection={false}
                />
            </div>

        </>
    );
};

export default TeamAllocationSettings;
